import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Save, Info, Hash, Zap, PenLine, ShieldOff, HelpCircle, CheckCircle2 } from "lucide-react";
import { catalogApi, type CatalogSettings } from "@/lib/api/catalog";
import { useCatalogSettings, CATALOG_SETTINGS_QUERY_KEY } from "@/hooks/useCatalogSettings";
import { useToast } from "@/contexts/ToastContext";
import { cn } from "@/lib/utils";

const AccessionNumberSetupPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<CatalogSettings>({
    accessionNumberMode: "AUTO",
    startingAccessionNumber: 1,
    approvalWorkflow: "disabled",
  });
  const [originalSettings, setOriginalSettings] = useState<CatalogSettings | null>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showModeChangeDialog, setShowModeChangeDialog] = useState(false);

  const { data: catalogSettings, isLoading: loading } = useCatalogSettings();

  useEffect(() => {
    if (catalogSettings && originalSettings === null) {
      setSettings(catalogSettings);
      setOriginalSettings(catalogSettings);
    }
  }, [catalogSettings, originalSettings]);

  const hasChanges =
    originalSettings !== null &&
    (settings.accessionNumberMode !== originalSettings.accessionNumberMode ||
      settings.startingAccessionNumber !== originalSettings.startingAccessionNumber);

  const isModeChanging =
    originalSettings !== null &&
    settings.accessionNumberMode !== originalSettings.accessionNumberMode;

  const handleSaveClick = () => {
    // If mode is changing, show confirmation dialog first
    if (isModeChanging) {
      setShowModeChangeDialog(true);
    } else {
      performSave();
    }
  };

  const performSave = async () => {
    try {
      setSaving(true);
      await catalogApi.updateCatalogSettings({
        accessionNumberMode: settings.accessionNumberMode,
        startingAccessionNumber: settings.startingAccessionNumber,
      });
      toast.success("Accession number settings saved successfully");
      queryClient.invalidateQueries(CATALOG_SETTINGS_QUERY_KEY);
      setOriginalSettings(settings);
    } catch (error: any) {
      console.error("Failed to save settings:", error);
      toast.error(error?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (mode: "AUTO" | "MANUAL") => {
    setSettings((prev) => ({ ...prev, accessionNumberMode: mode }));
  };

  const handleStartingNumberChange = (value: string) => {
    const num = parseInt(value) || 1;
    setSettings((prev) => ({ ...prev, startingAccessionNumber: Math.max(1, num) }));
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      navigate("/setup");
    }
  };

  const handleDiscard = () => {
    navigate("/setup");
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage accession number settings.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Accession Numbers"
          description="Configure how accession numbers are assigned to catalog items"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Accession Numbers" }]}
          showSearch={false}
        />

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner size="xl" label="Loading settings..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Status Card */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card className="p-4 bg-white border border-gray-200">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "flex items-center justify-center w-10 h-10 rounded-full",
                      settings.accessionNumberMode === "AUTO" ? "bg-green-100" : "bg-primary/10"
                    )}
                  >
                    {settings.accessionNumberMode === "AUTO" ? (
                      <Zap className="w-5 h-5 text-green-600" />
                    ) : (
                      <PenLine className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Current Mode</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {settings.accessionNumberMode === "AUTO" ? "Automatic" : "Manual Entry"}
                    </p>
                  </div>
                </div>
              </Card>

              {settings.accessionNumberMode === "AUTO" && (
                <Card className="p-4 bg-white border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full">
                      <Hash className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Next Accession #</p>
                      <p className="text-lg font-semibold font-mono text-gray-900">
                        {settings.startingAccessionNumber || 1}
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Mode Selection */}
            <Card className="overflow-hidden bg-white">
              <div className="p-6">
                <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                  <Hash className="w-5 h-5 text-primary" />
                  Accession Number Mode
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  Choose how accession numbers are assigned when cataloging new items.
                </p>

                <div className="space-y-4">
                  {/* Automatic Mode */}
                  <label
                    htmlFor="mode-auto"
                    className={cn(
                      "flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      settings.accessionNumberMode === "AUTO"
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <input
                      type="radio"
                      id="mode-auto"
                      name="accession-mode"
                      value="AUTO"
                      checked={settings.accessionNumberMode === "AUTO"}
                      onChange={() => handleModeChange("AUTO")}
                      className="mt-1 w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Zap className="w-5 h-5 text-amber-500" />
                        <span className="font-semibold text-gray-900">Automatic Generation</span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          Recommended
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        The system automatically generates sequential accession numbers for new
                        items. You can still enter a specific number when needed.
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          Best for new libraries
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          Faster cataloging
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          Override when needed
                        </span>
                      </div>
                    </div>
                  </label>

                  {/* Manual Mode */}
                  <label
                    htmlFor="mode-manual"
                    className={cn(
                      "flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      settings.accessionNumberMode === "MANUAL"
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <input
                      type="radio"
                      id="mode-manual"
                      name="accession-mode"
                      value="MANUAL"
                      checked={settings.accessionNumberMode === "MANUAL"}
                      onChange={() => handleModeChange("MANUAL")}
                      className="mt-1 w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <PenLine className="w-5 h-5 text-primary" />
                        <span className="font-semibold text-gray-900">Manual Entry Required</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        You must enter an accession number for each copy during cataloging. Ideal
                        for libraries digitizing existing collections with pre-assigned numbers.
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                          For existing collections
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                          Preserve your numbering
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                          Full control
                        </span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </Card>

            {/* Starting Number (only for AUTO mode) */}
            {settings.accessionNumberMode === "AUTO" && (
              <Card className="overflow-hidden bg-white">
                <div className="p-6">
                  <h2 className="flex items-center gap-2 mb-4 text-lg font-semibold text-gray-900">
                    <Hash className="w-5 h-5 text-primary" />
                    Next Accession Number
                  </h2>
                  <p className="mb-4 text-sm text-gray-600">
                    Set the starting point for automatic accession number generation.
                  </p>

                  <div className="max-w-xs">
                    <Label htmlFor="startingNumber">Next Number to Assign</Label>
                    <Input
                      id="startingNumber"
                      type="number"
                      min="1"
                      value={settings.startingAccessionNumber || 1}
                      onChange={(e) => handleStartingNumberChange(e.target.value)}
                      className="mt-1.5 text-lg font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      The next item cataloged will receive this accession number.
                    </p>
                  </div>

                  <div className="flex items-start gap-3 p-3 mt-4 bg-primary/5 border border-primary/10 rounded-lg">
                    <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-primary">
                      If migrating from another system, set this to the next number after your
                      highest existing accession number to avoid duplicates.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Help Text */}
            <Card className="p-4 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 mb-2">How does this work?</p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      <strong className="text-gray-700">Automatic mode:</strong> When adding new
                      items, accession numbers are assigned automatically. You can still enter a
                      specific number to override the auto-generated one.
                    </p>
                    <p>
                      <strong className="text-gray-700">Manual mode:</strong> You must enter an
                      accession number for each copy. The system will not allow submission without
                      valid accession numbers.
                    </p>
                    <p>
                      <strong className="text-gray-700">Multiple copies:</strong> When adding
                      multiple copies of a book, you can configure each copy's accession number,
                      condition, and status individually.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSaveClick} disabled={saving || !hasChanges} className="gap-2">
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </PageContainer>

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscard}
        variant="warning"
      />

      {/* Mode Change Confirmation */}
      <ConfirmDialog
        open={showModeChangeDialog}
        onOpenChange={setShowModeChangeDialog}
        title="Change Accession Mode?"
        description={
          settings.accessionNumberMode === "MANUAL"
            ? "Switching to Manual mode means you'll need to enter accession numbers for every copy when cataloging. This change takes effect immediately."
            : "Switching to Automatic mode means the system will generate accession numbers automatically. You can still override them when needed. This change takes effect immediately."
        }
        confirmLabel="Yes, Change Mode"
        cancelLabel="Cancel"
        onConfirm={performSave}
        variant="warning"
      />
    </AppLayout>
  );
};

export default AccessionNumberSetupPage;
